import React, { useEffect, useState, useCallback } from "react";
import { getCustomerPackages } from "../lib/packages";
import { getCustomerClaims } from "../lib/claims";
import { getCustomerRequests, REQUEST_SERVICES } from "../lib/requests";
import { getCustomerInvoices } from "../lib/invoices";
import { getCustomerShopOrders } from "../lib/shopOrders";
import { getBalance, getTransactions } from "../lib/balance";
import { WAREHOUSE_LIST, UNITS } from "../lib/warehouses";
import { useAuth } from "../context/AuthContext";
import { StatCard, StatusPill, PkgThumb, EmptyState, Spinner, toast, formatDate } from "../components/UI";
import PackageDetailModal from "../components/PackageDetailModal";
import ClaimModal from "../components/ClaimModal";
import RequestModal from "../components/RequestModal";
import ShippingRateCard from "../components/ShippingRateCard";
import InvoiceModal from "../components/InvoiceModal";
import ShopOrderModal from "../components/ShopOrderModal";
import PackageDetailsModal from "../components/PackageDetailsModal";
import WarehouseAddressCard from "../components/WarehouseAddressCard";
import { detectCarrier, getTrackingURL } from "../lib/carrier";

const CLAIM_STATUS_STYLE  = { "Open": { background: "#E6F1FB", color: "#185FA5" }, "In Review": { background: "#FAEEDA", color: "#854F0B" }, "Resolved": { background: "#E1F5EE", color: "#0F6E56" }, "Rejected": { background: "#FCEBEB", color: "#A32D2D" } };
const REQ_STATUS_STYLE    = { "Pending": { background: "#E6F1FB", color: "#185FA5" }, "In Progress": { background: "#FAEEDA", color: "#854F0B" }, "Completed": { background: "#E1F5EE", color: "#0F6E56" }, "Rejected": { background: "#FCEBEB", color: "#A32D2D" } };
const TYPE_LABELS         = { not_received: "Package not received at warehouse", not_delivered: "Package shipped but not delivered", damaged: "Package arrived damaged", other: "Other issue" };

export default function CustomerPage() {
  const { user } = useAuth();
  const [warehouse,  setWarehouse]  = useState("USA");
  const [packages,   setPackages]   = useState([]);
  const [claims,     setClaims]     = useState([]);
  const [requests,   setRequests]   = useState([]);
  const [invoices,   setInvoices]   = useState([]);
  const [shopOrders, setShopOrders] = useState([]);
  const [balance,    setBalance]    = useState(0);
  const [transactions,setTransactions]=useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [activeTab,  setActiveTab]  = useState("received");
  const [detailPkg,  setDetailPkg]  = useState(null);
  const [showClaim,  setShowClaim]  = useState(false);
  const [showReq,    setShowReq]    = useState(false);
  const [showShop,   setShowShop]   = useState(false);
  const [detailsPkg, setDetailsPkg] = useState(null);
  const [viewInvoice,setViewInvoice]= useState(null);

  const load = useCallback(async (wh) => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const [pkgs, clms, reqs, invs, orders, bal, txns] = await Promise.all([
        getCustomerPackages(user.email, wh || warehouse).catch(() => []),
        getCustomerClaims(user.email).catch(() => []),
        getCustomerRequests(user.email).catch(() => []),
        getCustomerInvoices(user.email).catch(() => []),
        getCustomerShopOrders(user.email).catch(() => []),
        getBalance(user.email).catch(() => 0),
        getTransactions(user.email).catch(() => []),
      ]);
      setPackages(pkgs || []); setClaims(clms || []); setRequests(reqs || []);
      setInvoices(invs || []); setShopOrders(orders || []); setBalance(bal || 0); setTransactions(txns || []);
    } catch (e) { toast("Failed to load: " + e.message, "error"); }
    finally { setLoading(false); }
  }, [user, warehouse]);

  useEffect(() => { load(warehouse); }, [user, warehouse]);

  const switchWarehouse = (wh) => { setWarehouse(wh); setActiveTab("received"); setSearch(""); };

  const q         = search.toLowerCase();
  const received  = packages.filter(p => p.status === "Received"  && (!q || (p.id+(p.description||"")).toLowerCase().includes(q)));
  const shipped   = packages.filter(p => p.status === "Shipped"   && (!q || (p.id+(p.description||"")+(p.shipmentTracking||"")).toLowerCase().includes(q)));
  const delivered = packages.filter(p => p.status === "Delivered" && (!q || (p.id+(p.description||"")).toLowerCase().includes(q)));
  const openClaims    = claims.filter(c => c.status === "Open" || c.status === "In Review");
  const openRequests  = requests.filter(r => r.status === "Pending" || r.status === "In Progress");
  const unpaidInvoices= invoices.filter(i => i.status === "Unpaid");

  const TrackingTag = ({ tracking, label }) => {
    if (!tracking) return null;
    const carrier = detectCarrier(tracking);
    const url     = carrier ? getTrackingURL(tracking, carrier.code) : null;
    return (
      <span className="pkg-detail" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ opacity: .6 }}>{label}:</span>
        <span style={{ fontFamily: "monospace", fontSize: 11 }}>{tracking}</span>
        {url && <a href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, fontWeight: 700, textDecoration: "none", color: carrier?.color || "var(--green)", background: carrier?.bg || "var(--green-light)", padding: "1px 6px", borderRadius: 10 }}>Track on {carrier?.name} ↗</a>}
      </span>
    );
  };

  const PkgCard = ({ pkg }) => (
    <div className="pkg-card pkg-card-clickable" onClick={() => setDetailPkg(pkg)}>
      <PkgThumb photoURL={pkg.photoURL} size={80} />
      <div className="pkg-info">
      <div className="pkg-id">{pkg.trackingNumber || pkg.id}</div>
        <div className="pkg-name">{pkg.description || "Package"}</div>
        <div className="pkg-meta">
          <StatusPill status={pkg.status} />
          {pkg.unit && <span style={{ fontSize: 11, background: "var(--bg-tertiary)", padding: "1px 8px", borderRadius: 10, fontWeight: 600 }}>{UNITS[pkg.unit]?.label || pkg.unit}</span>}
          <span className="pkg-detail">⚖ {pkg.weight} lbs{pkg.dimensions ? ` · 📐 ${pkg.dimensions}` : ""}</span>
          {pkg.eta && <span className="pkg-detail">📅 Est. delivery: {formatDate(pkg.eta)}</span>}
          {pkg.trackingNumber   && <TrackingTag tracking={pkg.trackingNumber}   label="Incoming" />}
          {pkg.shipmentTracking && <TrackingTag tracking={pkg.shipmentTracking} label="Shipment" />}
        </div>
        {/* Item details */}
        <div onClick={e => e.stopPropagation()}>
          {pkg.detailsEntered ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, background: "var(--green-light)", color: "var(--green-dark)", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>✓ {pkg.itemName} · ${pkg.itemValue}</span>
              {pkg.invoiceURL && <a href={pkg.invoiceURL} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--blue)" }}>View invoice ↗</a>}
              <button className="btn btn-ghost btn-sm" onClick={() => setDetailsPkg(pkg)} style={{ fontSize: 11, padding: "2px 8px" }}>Edit</button>
            </div>
          ) : (
            <button onClick={() => setDetailsPkg(pkg)} style={{ marginTop: 6, fontSize: 11, padding: "4px 10px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>
              ⚠ Enter item details
            </button>
          )}
        </div>
      </div>
      <div className="pkg-chevron">›</div>
    </div>
  );

  const TABS = [
    { key: "received",  label: "At warehouse", count: packages.filter(p => p.status === "Received").length,  cs: { background: "var(--blue-light)", color: "var(--blue-dark)" } },
    { key: "shipped",   label: "Shipped",      count: packages.filter(p => p.status === "Shipped").length,   cs: { background: "var(--amber-light)", color: "var(--amber-dark)" } },
    { key: "delivered", label: "Delivered",    count: packages.filter(p => p.status === "Delivered").length, cs: { background: "var(--green-light)", color: "var(--green-dark)" } },
    { key: "invoices",  label: "Invoices",     count: unpaidInvoices.length, cs: { background: "#FCEBEB", color: "#A32D2D" } },
    { key: "requests",  label: "Requests",     count: openRequests.length,   cs: { background: "var(--amber-light)", color: "var(--amber-dark)" } },
    { key: "claims",    label: "Claims",       count: openClaims.length,     cs: { background: "#FCEBEB", color: "#A32D2D" } },
    { key: "shop",      label: "Shop for me",  count: 0 },
    { key: "shipping",  label: "Shipping rate",count: 0 },
  ];

  const invoicePkgs = (inv) => packages.filter(p => inv.packageIds?.includes(p.id));
  const invoiceReqs = (inv) => requests.filter(r => inv.packageIds?.includes(r.packageId));

  return (
    <div className="page-wrap">
      <div className="page-header">
        <h2>My Packages</h2>
        <p>Welcome back, {user?.name || user?.email?.split("@")[0]}</p>
      </div>

      {/* Warehouse switcher */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {WAREHOUSE_LIST.map(wh => (
          <button key={wh.id} onClick={() => switchWarehouse(wh.id)} style={{
            padding: "8px 16px", borderRadius: 20, border: "1.5px solid",
            borderColor: warehouse === wh.id ? "var(--green)" : "var(--border-md)",
            background: warehouse === wh.id ? "var(--green-light)" : "var(--bg)",
            color: warehouse === wh.id ? "var(--green-dark)" : "var(--text-muted)",
            cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            transition: "all .15s",
          }}>
            {wh.flag} {wh.label}
          </button>
        ))}
      </div>

      {/* Warehouse address card */}
      <WarehouseAddressCard warehouseId={warehouse} customerName={user?.name} />

      <div className="stats-grid stats-grid-3">
        <StatCard label="Total packages" value={packages.length} />
        <StatCard label="In transit"     value={packages.filter(p => p.status === "Shipped").length}   color="#854F0B" />
        <StatCard label="Unpaid invoices" value={unpaidInvoices.length} color={unpaidInvoices.length > 0 ? "#A32D2D" : undefined} />
      </div>

      <div className="search-bar">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search packages, tracking numbers..." />
      </div>

      <div className="tab-bar" style={{ overflowX: "auto" }}>
        {TABS.map(t => (
          <div key={t.key} className={`tab-item ${activeTab === t.key ? "active" : ""}`} onClick={() => setActiveTab(t.key)} style={{ whiteSpace: "nowrap" }}>
            {t.label}
            {t.count > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 20, ...t.cs }}>{t.count}</span>}
          </div>
        ))}
      </div>

      {loading ? <Spinner msg="Loading..." /> : (
        <>
          {activeTab === "received"  && (received.length  === 0 ? <EmptyState msg="No packages at this warehouse yet." />  : received.map(p  => <PkgCard key={p.id} pkg={p} />))}
          {activeTab === "shipped"   && (shipped.length   === 0 ? <EmptyState msg="No packages in transit." />             : shipped.map(p   => <PkgCard key={p.id} pkg={p} />))}
          {activeTab === "delivered" && (delivered.length === 0 ? <EmptyState msg="No delivered packages yet." />          : delivered.map(p => <PkgCard key={p.id} pkg={p} />))}

          {activeTab === "invoices" && (
            invoices.length === 0 ? <EmptyState msg="No invoices yet." /> :
            invoices.map(inv => (
              <div className="card" key={inv.id} style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => setViewInvoice(inv)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text-muted)", marginBottom: 2 }}>{inv.invoiceNumber}</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>${inv.totalDue}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{inv.packageIds?.length} package{inv.packageIds?.length !== 1 ? "s" : ""} · {formatDate(inv.createdAt)}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: inv.status === "Paid" ? "var(--green-light)" : "#FAEEDA", color: inv.status === "Paid" ? "var(--green-dark)" : "#854F0B" }}>
                      {inv.status === "Paid" ? "✓ Paid" : "⏳ Unpaid"}
                    </span>
                    {inv.status === "Unpaid" && <span style={{ fontSize: 12, color: "var(--green-dark)", fontWeight: 600 }}>Pay via Whish →</span>}
                  </div>
                </div>
              </div>
            ))
          )}

          {activeTab === "requests" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button className="btn btn-primary" onClick={() => setShowReq(true)}>+ New request</button>
              </div>
              {requests.length === 0 ? <EmptyState msg="No requests yet." /> :
              requests.map(r => {
                const style     = REQ_STATUS_STYLE[r.status] || REQ_STATUS_STYLE["Pending"];
                const svcLabels = r.services?.map(id => { const s = REQUEST_SERVICES.find(x => x.id === id); return s ? `${s.icon} ${s.label}` : id; }) || [];
                return (
                  <div className="card" key={r.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {svcLabels.map((s, i) => <span key={i} style={{ background: "var(--green-light)", color: "var(--green-dark)", fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>{s}</span>)}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                        <span className="status-pill" style={style}>{r.status}</span>
                        {r.totalCost > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: "var(--green-dark)" }}>${r.totalCost}</span>}
                      </div>
                    </div>
                    {r.note && <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>{r.note}</p>}
                    {r.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>LebMarket: </span>{r.adminNotes}</div>}
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 8 }}>{formatDate(r.createdAt)}</div>
                  </div>
                );
              })}
            </>
          )}

          {activeTab === "claims" && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button className="btn btn-primary" onClick={() => setShowClaim(true)}>+ Open a claim</button>
              </div>
              {claims.length === 0 ? <EmptyState msg="No claims yet." /> :
              claims.map(c => {
                const style = CLAIM_STATUS_STYLE[c.status] || CLAIM_STATUS_STYLE["Open"];
                return (
                  <div className="card" key={c.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{TYPE_LABELS[c.type] || c.type}</div>
                      <span className="status-pill" style={style}>{c.status}</span>
                    </div>
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: c.adminNotes ? 8 : 0 }}>{c.description}</p>
                    {c.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>LebMarket: </span>{c.adminNotes}</div>}
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 8 }}>{formatDate(c.createdAt)}</div>
                  </div>
                );
              })}
            </>
          )}

          {activeTab === "shop" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: "var(--text-muted)" }}>We buy it, you receive it at your door in Lebanon.</p>
                <button className="btn btn-primary" onClick={() => setShowShop(true)}>+ New order</button>
              </div>
              {shopOrders.length === 0 ? <EmptyState msg="No shop orders yet." /> :
              shopOrders.map(o => {
                const statusStyle = { "Pending": { background: "#E6F1FB", color: "#185FA5" }, "Confirmed": { background: "#FAEEDA", color: "#854F0B" }, "Ordered": { background: "#FAEEDA", color: "#854F0B" }, "Arrived": { background: "#E1F5EE", color: "#0F6E56" }, "Rejected": { background: "#FCEBEB", color: "#A32D2D" } };
                const st = statusStyle[o.status] || statusStyle["Pending"];
                return (
                  <div className="card" key={o.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{o.items?.length} item{o.items?.length !== 1 ? "s" : ""}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatDate(o.createdAt)}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span className="status-pill" style={st}>{o.status}</span>
                        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>${o.grandTotal}</div>
                      </div>
                    </div>
                    {o.items?.map((item, i) => (
                      <div key={i} style={{ padding: "6px 0", borderTop: "0.5px solid var(--border)", fontSize: 13 }}>
                        <a href={item.link} target="_blank" rel="noreferrer" style={{ color: "var(--blue)", wordBreak: "break-all" }}>{item.link}</a>
                        <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>×{item.quantity} · ${item.total}</span>
                      </div>
                    ))}
                    {o.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13, marginTop: 10 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>LebMarket: </span>{o.adminNotes}</div>}
                  </div>
                );
              })}
            </>
          )}

          {activeTab === "shipping" && (
            <>
              <ShippingRateCard email={user.email} warehouse={warehouse} />
              <div className="card">
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>How shipping cost is calculated</div>
                <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.8 }}>
                  <div>1. Package weight in lbs is converted to kg</div>
                  <div>2. Weight in kg × your rate per kg = shipping cost</div>
                  <div>3. Unit 7000 = clothes, electronics, car parts</div>
                  <div>4. Unit 7500 = cosmetics (different rate may apply)</div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {detailPkg    && <PackageDetailModal pkg={detailPkg} onClose={() => setDetailPkg(null)} isAdmin={false} />}
      {showClaim    && <ClaimModal   user={user} packages={packages} onClose={() => setShowClaim(false)} onSaved={load} />}
      {showReq      && <RequestModal user={user} packages={packages} onClose={() => setShowReq(false)}   onSaved={load} />}
      {showShop     && <ShopOrderModal user={user} onClose={() => setShowShop(false)} onSaved={load} />}
      {detailsPkg   && <PackageDetailsModal pkg={detailsPkg} onClose={() => setDetailsPkg(null)} onSaved={load} />}
      {viewInvoice  && <InvoiceModal invoice={viewInvoice} packages={invoicePkgs(viewInvoice)} requests={invoiceReqs(viewInvoice)} isAdmin={false} onClose={() => setViewInvoice(null)} onPaid={load} />}
    </div>
  );
}
